import React, { useContext, useEffect, useState, useMemo } from 'react';
import { StoreContext } from '../../Context/StoreContext';
import { useMultiCart } from '../../Context/MultiCartContext';
import './PopularFoods.css';
import { assets } from '../../assets/assets';
import { useNavigate } from 'react-router-dom';
import FoodShimmer from '../common/FoodShimmer';
import { useTranslation } from 'react-i18next';

// Utility function to ensure consistent string keys for React
const getStringKey = (id, fallback = '') => {
    if (!id) return fallback;
    if (typeof id === 'string') return id;
    
    // Handle populated MongoDB documents
    if (typeof id === 'object' && id._id) {
        return id._id.toString();
    }
    
    if (id.toString && typeof id.toString === 'function') return id.toString();
    return String(id) || fallback;
};
// Import food images for fallbacks
import food_1 from '../../assets/food_1.png';
import food_2 from '../../assets/food_2.png';
import food_5 from '../../assets/food_5.png';
import food_9 from '../../assets/food_9.png';
import food_13 from '../../assets/food_13.png';
import food_17 from '../../assets/food_17.png';

const PopularFoods = () => {
  const { t } = useTranslation();
  const { food_list, currency, url, selectedRestaurant, isFoodLoading } = useContext(StoreContext);
  const { restaurantCarts, addToRestaurantCart, removeFromRestaurantCart } = useMultiCart();
  const [likedItems, setLikedItems] = useState({}); // Track liked items
  const [likeCounts, setLikeCounts] = useState({}); // Track like counts
  const navigate = useNavigate();
  
  // Create an array of fallback food images
  const fallbackImages = [food_1, food_2, food_5, food_9, food_13, food_17];

  // Memoize popular foods calculation to prevent unnecessary re-renders
  const popularFoods = useMemo(() => {
    if (!food_list || food_list.length === 0) {
      return [];
    }

    // Take first 12 items from food_list as popular foods
    // In a real app, this would be based on order count, ratings, etc.
    // Note: Backend has endpoint `/api/food-management/restaurants/:restaurantId/popular-food` (singular)
    // but using existing food_list is more efficient and prevents unnecessary API calls
    const foods = food_list.slice(0, 12);
    
    return foods;
  }, [food_list, selectedRestaurant]);

  // Initialize like counts when popular foods change
  useEffect(() => {
    if (popularFoods.length > 0) {
      setLikeCounts(prevCounts => {
        const newCounts = { ...prevCounts };
        popularFoods.forEach(item => {
          // Only set initial count if not already set
          if (!(item._id in newCounts)) {
            newCounts[item._id] = Math.floor(Math.random() * 21) + 5; // 5-25 likes
          }
        });
        return newCounts;
      });
    }
  }, [popularFoods]);

  // Handle like functionality
  const handleLike = (e, itemId) => {
    e.stopPropagation();
    setLikedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
    
    // Update like count
    setLikeCounts(prev => ({
      ...prev,
      [itemId]: prev[itemId] + (likedItems[itemId] ? -1 : 1)
    }));
  };

  // Format image URL correctly
  const getImageUrl = (image, index) => {
    if (!image) {
      // Use a fallback image based on index for variety
      return fallbackImages[index % fallbackImages.length];
    }
    
    // If image is a full URL, use it directly
    if (image.startsWith('http')) return image;
    
    // If image is a relative path, append the server URL
    return `${url}/uploads/${image}`;
  };

  // Helper function to get restaurant data for an item
  const getRestaurantData = (item) => {
    if (item.restaurant && typeof item.restaurant === 'object') {
      // Restaurant is populated
      return item.restaurant;
    }
    return null;
  };

  // Helper function to get restaurant ID for an item
  const getRestaurantId = (item) => {
    if (item.restaurant && typeof item.restaurant === 'object' && item.restaurant._id) {
      return item.restaurant._id;
    } else if (item.restaurant && typeof item.restaurant === 'string') {
      return item.restaurant;
    }
    return null;
  };

  // Check restaurant operational status for a specific restaurant
  const getRestaurantStatus = (restaurant) => {
    if (!restaurant) {
      return { isOpen: true }; // If no restaurant data, assume open
    }
    
    // Check for manual override first
    if (restaurant.operationalStatus?.override && restaurant.operationalStatus.override !== 'auto') {
      const isOpen = restaurant.operationalStatus.override === 'open';
      return { isOpen };
    }

    // Fall back to automatic status based on business hours
    if (!restaurant.businessHours || restaurant.businessHours.length === 0) {
      return { isOpen: true }; // If no hours set, assume open
    }

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const todayHours = restaurant.businessHours.find(hours => hours.day === currentDay);
    
    if (!todayHours) {
      return { isOpen: false }; // Closed today
    }

    const [openHour, openMin] = todayHours.open.split(':').map(Number);
    const [closeHour, closeMin] = todayHours.close.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    const isOpen = !(currentTime < openTime || currentTime > closeTime);

    return { isOpen };
  };

  // Helper function to check if item is in cart and get quantity
  const getItemCartInfo = (item) => {
    const restaurantId = getRestaurantId(item);
    if (!restaurantId) return { isInCart: false, quantity: 0 };

    const restaurantCart = restaurantCarts.find(cart => cart.restaurantId === restaurantId);
    const cartItem = restaurantCart?.items.find(cartItem => cartItem.foodId === item._id);
    
    return {
      isInCart: cartItem && cartItem.quantity > 0,
      quantity: cartItem ? cartItem.quantity : 0
    };
  };

  // Helper function to check if item can be ordered (both item availability and restaurant status)
  const canOrderItem = (item) => {
    // Check if item itself is available
    if (item.isAvailable === false) {
      return { canOrder: false, reason: 'out-of-stock' };
    }

    // Check if restaurant is open
    const restaurantData = getRestaurantData(item);
    const restaurantStatus = getRestaurantStatus(restaurantData);
    
    if (!restaurantStatus.isOpen) {
      return { canOrder: false, reason: 'restaurant-closed' };
    }

    return { canOrder: true, reason: 'available' };
  };

  const handleAddToCart = async (e, item) => {
    e.stopPropagation();
    const orderStatus = canOrderItem(item);
    
    // Only add to cart if item is available and restaurant is open
    if (orderStatus.canOrder) {
      const restaurantId = getRestaurantId(item);
      if (restaurantId) {
        await addToRestaurantCart(item._id, restaurantId, {
          name: item.name,
          price: item.price,
          image: item.image,
          restaurantName: item.restaurant?.name || item.restaurantName,
          restaurantImage: item.restaurant?.branding?.logo
        });
      }
    }
  };

  const handleRemoveFromCart = async (e, item) => {
    e.stopPropagation();
    const orderStatus = canOrderItem(item);
    
    // Only remove from cart if item is available and restaurant is open
    if (orderStatus.canOrder) {
      const restaurantId = getRestaurantId(item);
      if (restaurantId) {
        await removeFromRestaurantCart(item._id, restaurantId);
      }
    }
  };

  const navigateToItem = (item) => {
    // Navigate to item detail view if you have one
    // For now, just add to cart if available
    const orderStatus = canOrderItem(item);
    if (orderStatus.canOrder) {
      handleAddToCart({ stopPropagation: () => {} }, item);
    }
  };

  return (
    <div id="popular-dishes" className="popular-foods-container">
      <div className="popular-foods-header">
        <h2>{t('food.popular_dishes')}</h2>
      </div>
      
      {isFoodLoading ? (
        <div className="popular-foods-grid">
          {Array.from({ length: 10 }, (_, index) => (
            <FoodShimmer key={`shimmer-${index}`} />
          ))}
        </div>
      ) : popularFoods.length === 0 ? (
        <div className="popular-foods-empty">
          <p>{t('food.popular_dishes')} will appear here soon!</p>
        </div>
      ) : (
        <div className="popular-foods-grid">
          {popularFoods.map((item, index) => {
            const orderStatus = canOrderItem(item);
            const cartInfo = getItemCartInfo(item);
            const isUnavailable = !orderStatus.canOrder;
            const isOutOfStock = orderStatus.reason === 'out-of-stock';
            
            return (
              <div 
                key={getStringKey(item._id, `popular-${index}`)} 
                className={`popular-food-card ${isOutOfStock ? 'popular-food-unavailable' : ''}`}
                onClick={() => navigateToItem(item)}
              >
                <div className="popular-food-image">
                  <img src={getImageUrl(item.image, index)} alt={item.name} />
                  
                  {/* Only show overlay for out-of-stock items, not restaurant-closed */}
                  {isOutOfStock && (
                    <div className="popular-food-unavailable-overlay">
                      <div className="unavailable-content">
                        <span className="unavailable-text">{t('food.out_of_stock')}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Like Button with Count */}
                  <div 
                    className={`popular-food-like ${likedItems[item._id] ? 'liked' : ''}`} 
                    onClick={(e) => handleLike(e, item._id)}
                  >
                    <span className="heart-icon">{likedItems[item._id] ? '❤️' : '🤍'}</span>
                    <span className="like-count">{likeCounts[item._id] || 0}</span>
                  </div>
                  
                  {/* Price badge on top of image */}
                  <div className="popular-food-price-badge">
                    <p>{Math.round(item.price)} {currency}</p>
                  </div>
                  
                  {/* Only show add/counter buttons if item can be ordered */}
                  {orderStatus.canOrder && (
                    !cartInfo.isInCart
                      ? <div 
                          className='popular-food-add' 
                          onClick={(e) => handleAddToCart(e, item)}
                        ></div>
                      : <div className="popular-food-counter">
                          <img src={assets.remove_icon_red} onClick={(e) => handleRemoveFromCart(e, item)} alt="" />
                          <p>{cartInfo.quantity}</p>
                          <img src={assets.add_icon_green} onClick={(e) => handleAddToCart(e, item)} alt="" />
                        </div>
                  )}
                </div>
                
                <div className="popular-food-info">
                  <div className="popular-food-title">
                    <h3>{item.name}</h3>
                  </div>
                  <p className="popular-food-description">{item.description}</p>
                  {(item.restaurant?.name || 
                    (selectedRestaurant && selectedRestaurant.name) || 
                    item.restaurantName) && (
                    <div className="popular-food-restaurant">
                      <span className="restaurant-tag">
                        {item.restaurant?.name || 
                         (selectedRestaurant && selectedRestaurant.name) || 
                         item.restaurantName}
                      </span>
                    </div>
                  )}
                  {/* Show availability status at the bottom only for restaurant-closed items */}
                  {isUnavailable && orderStatus.reason === 'restaurant-closed' && (
                    <div className="popular-food-availability-status">
                      <span className="status-indicator restaurant-closed">
                        ● {t('restaurants.closed')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PopularFoods; 